import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppBootLoader from "@/components/AppBootLoader";
import NaturalCursor from "@/components/NaturalCursor";
import ForestFauna from "@/components/ForestFauna";
import LandingPage from "./pages/LandingPage";

// Lazy-loaded routes — keeps the initial JS bundle small for faster FCP/LCP
const TherapistDirectory = lazy(() => import("./pages/TherapistDirectory"));
const TherapistProfile = lazy(() => import("./pages/TherapistProfile"));
const TherapistLayout = lazy(() => import("./layouts/TherapistLayout"));
const TherapistDashboard = lazy(() => import("./pages/TherapistDashboard"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const PatientsPage = lazy(() => import("./pages/PatientsPage"));
const TherapistSessionsPage = lazy(() => import("./pages/TherapistSessionsPage"));
const FinancialPage = lazy(() => import("./pages/FinancialPage"));
const TherapistWalletPage = lazy(() => import("./pages/TherapistWalletPage"));
const TherapistSubscriptionAsaas = lazy(() => import("./pages/TherapistSubscriptionAsaas"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const TherapistGroupsManagement = lazy(() => import("./pages/TherapistGroupsManagement"));
const TherapistOnboarding = lazy(() => import("./pages/TherapistOnboarding"));
const TherapistSettingsPage = lazy(() => import("./pages/TherapistSettingsPage"));
const TherapistProfileSettings = lazy(() => import("./pages/TherapistProfileSettings"));
const ReferralNetworkPage = lazy(() => import("./pages/ReferralNetworkPage"));
const MyReferralsPage = lazy(() => import("./pages/MyReferralsPage"));
const ReferralLandingPage = lazy(() => import("./pages/ReferralLandingPage"));
const TherapistLinkPage = lazy(() => import("./pages/TherapistLinkPage"));
const PatientDashboard = lazy(() => import("./pages/PatientDashboard"));
const EmotionalCheckup = lazy(() => import("./pages/EmotionalCheckup"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const PatientTasksPage = lazy(() => import("./pages/PatientTasksPage"));
const TestPage = lazy(() => import("./pages/TestPage"));
const AddictionTestPage = lazy(() => import("./pages/AddictionTestPage"));
const PatientAuth = lazy(() => import("./pages/PatientAuth"));
const TherapistAuth = lazy(() => import("./pages/TherapistAuth"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminTherapists = lazy(() => import("./pages/AdminTherapists"));
const AdminPatients = lazy(() => import("./pages/AdminPatients"));
const AdminSessions = lazy(() => import("./pages/AdminSessions"));
const AdminFinancial = lazy(() => import("./pages/AdminFinancial"));
const AdminPayments = lazy(() => import("./pages/AdminPayments"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminLogs = lazy(() => import("./pages/AdminLogs"));
const PatientGroupsPage = lazy(() => import("./pages/PatientGroupsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function ConditionalFauna() {
  const location = useLocation();
  const isTherapistRoute = location.pathname.startsWith("/terapeuta");
  const isAdminRoute = location.pathname.startsWith("/admin");
  if (isTherapistRoute || isAdminRoute) return null;
  return <ForestFauna />;
}

function AppBootGate({ children }: { children: React.ReactNode }) {
  const { isLoading } = useAuth();
  return <AppBootLoader isLoading={isLoading}>{children}</AppBootLoader>;
}


const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <NaturalCursor />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ConditionalFauna />
          <AppBootGate>
          <Suspense fallback={null}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/diretorio" element={<TherapistDirectory />} />
            <Route path="/terapeuta/perfil/:slug" element={<TherapistProfile />} />
            <Route path="/checkup" element={<EmotionalCheckup />} />
            <Route path="/ferramentas" element={<ToolsPage />} />
            <Route path="/ferramentas/vicios" element={<AddictionTestPage />} />
            <Route path="/ferramentas/:slug" element={<TestPage />} />
            <Route path="/r/:code" element={<ReferralLandingPage />} />
            <Route path="/t/:slug" element={<TherapistLinkPage />} />

            {/* Auth routes */}
            <Route path="/paciente/auth" element={<PatientAuth />} />
            <Route path="/terapeuta/auth" element={<TherapistAuth />} />

            {/* Admin login */}
            <Route path="/admin" element={<AdminLogin />} />

            {/* Admin protected routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["owner"]} redirectTo="/admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="terapeutas" element={<AdminTherapists />} />
              <Route path="pacientes" element={<AdminPatients />} />
              <Route path="sessoes" element={<AdminSessions />} />
              <Route path="financeiro" element={<AdminFinancial />} />
              <Route path="pagamentos" element={<AdminPayments />} />
              <Route path="usuarios" element={<AdminUsersPage />} />
              <Route path="logs" element={<AdminLogs />} />
            </Route>

            {/* Therapist onboarding (protected, outside layout) */}
            <Route
              path="/terapeuta/onboarding"
              element={
                <ProtectedRoute allowedRoles={["therapist"]} redirectTo="/terapeuta/auth">
                  <TherapistOnboarding />
                </ProtectedRoute>
              }
            />

            {/* Therapist routes (protected, no subscription gate) */}
            <Route
              path="/terapeuta"
              element={
                <ProtectedRoute allowedRoles={["therapist"]} redirectTo="/terapeuta/auth">
                  <TherapistLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<TherapistDashboard />} />
              <Route path="agenda" element={<CalendarPage />} />
              <Route path="sessoes" element={<TherapistSessionsPage />} />
              <Route path="pacientes" element={<PatientsPage />} />
              <Route path="financeiro" element={<FinancialPage />} />
              <Route path="carteira" element={<TherapistWalletPage />} />
              <Route path="assinatura" element={<TherapistSubscriptionAsaas />} />
              <Route path="servicos" element={<ServicesPage />} />
              <Route path="grupos" element={<TherapistGroupsManagement />} />
              
              <Route path="indicacoes" element={<ReferralNetworkPage />} />
              <Route path="indicacoes/minhas" element={<MyReferralsPage />} />
              <Route path="configuracoes" element={<TherapistSettingsPage />} />
              <Route path="perfil" element={<TherapistProfileSettings />} />
            </Route>

            {/* Patient routes (protected) */}
            <Route
              path="/paciente/dashboard"
              element={
                <ProtectedRoute allowedRoles={["patient"]} redirectTo="/paciente/auth">
                  <PatientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/paciente/grupos"
              element={
                <ProtectedRoute allowedRoles={["patient"]} redirectTo="/paciente/auth">
                  <PatientGroupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/paciente/tarefas/:therapistId"
              element={
                <ProtectedRoute allowedRoles={["patient"]} redirectTo="/paciente/auth">
                  <PatientTasksPage />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </AppBootGate>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
